import { Dispatch, SetStateAction, useState } from 'react';

import { useCheckMatchingContact, useMutateContactsApi } from '~/controllers/contacts';
import { ApplicationPayload } from '~/types/ApplicationPayload';
import { Contacts } from '~/types/Contacts';
import { Person } from '~/types/Person';

interface ContactAddOptions<TContact> {
  sourceOfEditingValue: string;
  setSourceOfEditing: Dispatch<SetStateAction<string>>;
  onUpdate: (contact: TContact) => void;
  getNewContact: (newValues: ApplicationPayload) => TContact;
  getNewContactData: (newContact: TContact) => any;
  setContactIds: (newContact: TContact, contactId: string) => void;
  setNewContactUserRoles: (newContact: TContact, userRoles: Array<string>) => void;
}

export function useContactAdd<TContact>(options: ContactAddOptions<TContact>) {
  const {
    sourceOfEditingValue,
    setSourceOfEditing,
    onUpdate,
    getNewContact,
    getNewContactData,
    setContactIds,
    setNewContactUserRoles,
  } = options;

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [contactState, setContactState] = useState<{
    matchingContactList: Person[];
    selectedContact: Person | null;
    newContact: TContact | null;
  }>({ matchingContactList: [], selectedContact: null, newContact: null });

  const contactsMutation = useMutateContactsApi();
  const checkMatchingContact = useCheckMatchingContact();

  const resetContactState = () => setContactState({ matchingContactList: [], selectedContact: null, newContact: null });

  const updateContactAndClose = (contact: TContact) => {
    const contactData = getNewContactData(contact);
    contactsMutation.mutate(contactData);
    onUpdate(contact);
    setSourceOfEditing(sourceOfEditingValue);
    resetContactState();
    setModalOpen(false);
    setDrawerOpen(false);
  };

  const handleSelectContact = () => {
    const { selectedContact, newContact } = contactState;
    if (selectedContact && newContact) {
      setSourceOfEditing(sourceOfEditingValue);
      const updatedContact = { ...newContact };
      setContactIds(updatedContact, selectedContact.contactId);
      setNewContactUserRoles(updatedContact, selectedContact.userRoles!);
      updateContactAndClose(updatedContact);
    }
  };

  const handleCreateNewContact = () => {
    if (contactState.newContact) {
      updateContactAndClose(contactState.newContact);
    }
  };

  const onFinish = async (newValues: ApplicationPayload) => {
    const newContact = getNewContact(newValues);
    const newContactData = getNewContactData(newContact);
    const matchingContactList = await checkMatchingContact.mutateAsync(newContactData);
    const matchType = matchingContactList[0]?.matchType;
    const contactIdList = matchingContactList.map((contact: Contacts['contacts']) => contact.contactId);
    const matchingContactUserRoles = matchingContactList.map((contact: Contacts['contacts']) => contact.userRoles);

    switch (matchType) {
      case 'FULL_MATCH':
        if (contactIdList.length > 0) {
          setContactIds(newContact, contactIdList[0]);
          setNewContactUserRoles(newContact, matchingContactUserRoles[0]);
          updateContactAndClose(newContact);
        } else {
          updateContactAndClose(newContact);
        }
        break;
      case 'PARTIAL_MATCH':
        setContactState({
          matchingContactList,
          selectedContact: null,
          newContact,
        });
        setModalOpen(true);
        break;
      default:
        updateContactAndClose(newContact);
    }
  };

  const setSelectedContact = (contact: Person) => setContactState((prev) => ({ ...prev, selectedContact: contact }));

  return {
    drawerOpen,
    setDrawerOpen,
    modalOpen,
    onCancelModal: () => {
      setModalOpen(false);
      resetContactState();
    },
    onCloseDrawer: () => {
      setDrawerOpen(false);
      resetContactState();
    },
    contactState,
    setSelectedContact,
    handleCreateNewContact,
    handleSelectContact,
    onFinish,
  };
}
